import React, { useState } from 'react';
import { Calendar } from 'lucide-react';
import { Link } from 'react-router-dom';
import { SEO } from '../components/SEO';

type CalculationType = 'difference' | 'add' | 'subtract' | 'workdays';

interface DateState {
  startDate: string;
  endDate: string;
  days: string;
  calculationType: CalculationType;
  includeEndDate: boolean;
}

function KalkulatorDatuma() {
  const [values, setValues] = useState<DateState>({
    startDate: '',
    endDate: '',
    days: '',
    calculationType: 'difference',
    includeEndDate: true
  });

  const [result, setResult] = useState<{
    days: number;
    startDate?: Date;
    endDate?: Date;
    workdays?: number;
  } | null>(null);

  const isWeekend = (date: Date): boolean => {
    const day = date.getDay();
    return day === 0 || day === 6; // 0 is Sunday, 6 is Saturday
  };

  const addDays = (date: Date, days: number): Date => {
    const result = new Date(date);
    result.setDate(result.getDate() + days);
    return result;
  };

  const getWorkdaysBetweenDates = (startDate: Date, endDate: Date): number => {
    let workdays = 0;
    const currentDate = new Date(startDate);

    while (currentDate <= endDate) {
      if (!isWeekend(currentDate)) {
        workdays++;
      }
      currentDate.setDate(currentDate.getDate() + 1);
    }

    return workdays;
  };

  const calculateDates = () => {
    switch (values.calculationType) {
      case 'difference': {
        const start = new Date(values.startDate);
        const end = new Date(values.endDate);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const workdays = getWorkdaysBetweenDates(start, end);
        
        setResult({
          days: diffDays + (values.includeEndDate ? 1 : 0),
          startDate: start,
          endDate: end,
          workdays: workdays
        });
        break;
      }
      case 'add': {
        const start = new Date(values.startDate);
        const days = parseInt(values.days);
        if (!isNaN(days)) {
          const end = addDays(start, days - (values.includeEndDate ? 1 : 0));
          setResult({
            days,
            startDate: start,
            endDate: end
          });
        }
        break;
      }
      case 'subtract': {
        const start = new Date(values.startDate);
        const days = parseInt(values.days);
        if (!isNaN(days)) {
          const end = addDays(start, -days + (values.includeEndDate ? 1 : 0));
          setResult({
            days,
            startDate: start,
            endDate: end
          });
        }
        break;
      }
      case 'workdays': {
        const start = new Date(values.startDate);
        const end = new Date(values.endDate);
        const workdays = getWorkdaysBetweenDates(start, end);
        const diffTime = Math.abs(end.getTime() - start.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        
        setResult({
          days: diffDays + (values.includeEndDate ? 1 : 0),
          startDate: start,
          endDate: end,
          workdays: workdays
        });
        break;
      }
    }
  };

  const formatDate = (date: Date): string => {
    return date.toLocaleDateString('hr-HR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Datuma",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte razliku između datuma, dodajte ili oduzmite dane, i izračunajte radne dane. Jednostavan i precizan kalkulator datuma.",
    "url": "https://online-kalkulator.com/kalkulator-datuma",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun razlike između datuma",
      "Dodavanje i oduzimanje dana",
      "Izračun radnih dana",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Datuma | Izračun Dana Između Datuma"
        description="Izračunajte razliku između datuma, dodajte ili oduzmite dane, i izračunajte radne dane. Jednostavan i precizan kalkulator datuma."
        canonicalUrl="/kalkulator-datuma"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Calendar className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Datuma</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Calculation Type Selection */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Vrsta izračuna:
                </label>
                <select
                  value={values.calculationType}
                  onChange={(e) => setValues(prev => ({ 
                    ...prev, 
                    calculationType: e.target.value as CalculationType,
                    result: null
                  }))}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="difference">Razlika između datuma</option>
                  <option value="add">Dodaj dane</option>
                  <option value="subtract">Oduzmi dane</option>
                  <option value="workdays">Izračun radnih dana</option>
                </select>
              </div>

              {/* Input Fields */}
              <div className="space-y-4">
                {(values.calculationType === 'difference' || 
                  values.calculationType === 'add' || 
                  values.calculationType === 'subtract') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Početni datum
                    </label>
                    <input
                      type="date"
                      value={values.startDate}
                      onChange={(e) => setValues(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                )}

                {(values.calculationType === 'difference' || 
                  values.calculationType === 'workdays') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Završni datum
                    </label>
                    <input
                      type="date"
                      value={values.endDate}
                      onChange={(e) => setValues(prev => ({ ...prev, endDate: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                )}

                {(values.calculationType === 'add' || 
                  values.calculationType === 'subtract') && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Broj dana
                    </label>
                    <input
                      type="number"
                      value={values.days}
                      onChange={(e) => setValues(prev => ({ ...prev, days: e.target.value }))}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="Unesite broj dana"
                    />
                  </div>
                )}

                <div className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    id="includeEndDate"
                    checked={values.includeEndDate}
                    onChange={(e) => setValues(prev => ({ 
                      ...prev, 
                      includeEndDate: e.target.checked 
                    }))}
                    className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                  />
                  <label htmlFor="includeEndDate" className="text-sm text-gray-700">
                    Uključi završni datum
                  </label>
                </div>
              </div>

              {/* Calculate Button */}
              <button
                onClick={calculateDates}
                className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition"
              >
                Izračunaj
              </button>

              {/* Results */}
              {result && (
                <div className="space-y-4 border-t pt-4">
                  {values.calculationType === 'difference' && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Ukupno dana:</span>
                        <span className="text-xl font-bold text-blue-600">
                          {result.days}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Radnih dana:</span>
                        <span className="font-semibold">
                          {result.workdays}
                        </span>
                      </div>
                    </>
                  )}

                  {values.calculationType === 'workdays' && (
                    <>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Radnih dana:</span>
                        <span className="text-xl font-bold text-blue-600">
                          {result.workdays}
                        </span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600">Ukupno dana:</span>
                        <span className="font-semibold">
                          {result.days}
                        </span>
                      </div>
                    </>
                  )}

                  {(values.calculationType === 'add' || 
                    values.calculationType === 'subtract') && 
                    result.endDate && (
                    <div className="flex justify-between items-center">
                      <span className="text-gray-600">Rezultat:</span>
                      <span className="text-xl font-bold text-blue-600">
                        {formatDate(result.endDate)}
                      </span>
                    </div>
                  )}

                  {result.startDate && result.endDate && (
                    <div className="text-sm text-gray-500 space-y-1">
                      <div>Od: {formatDate(result.startDate)}</div>
                      <div>Do: {formatDate(result.endDate)}</div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Datuma</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator datuma je svestran alat koji vam omogućuje različite izračune s datumima.
                  Možete izračunati razliku između dva datuma, dodati ili oduzeti dane od određenog datuma,
                  te izračunati broj radnih dana između dva datuma.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Dostupni Izračuni</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Razlika između dva datuma (ukupni i radni dani)</li>
                  <li>Dodavanje određenog broja dana na datum</li>
                  <li>Oduzimanje određenog broja dana od datuma</li>
                  <li>Izračun radnih dana između dva datuma</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Odaberite vrstu izračuna iz padajućeg izbornika</li>
                  <li>Unesite potrebne datume ili broj dana</li>
                  <li>Označite "Uključi završni datum" ako želite uključiti i krajnji datum</li>
                  <li>Kliknite "Izračunaj" za dobivanje rezultata</li>
                  <li>Rezultat će prikazati tražene informacije ovisno o vrsti izračuna</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Radni dani ne uključuju vikende (subote i nedjelje)</li>
                  <li>Praznici nisu uključeni u izračun radnih dana</li>
                  <li>Možete uključiti ili isključiti završni datum u izračunu</li>
                  <li>Datumi se prikazuju u hrvatskom formatu</li>
                  <li>Izračuni su precizni do razine dana</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Primjeri Korištenja</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračun trajanja projekta</li>
                  <li>Planiranje rokova i isporuka</li>
                  <li>Izračun radnih dana za obračun plaće</li>
                  <li>Planiranje događaja i rezervacija</li>
                  <li>Izračun datuma isteka</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Povezani Kalkulatori</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Link 
                    to="/prijestopna-godina"
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                  >
                    <h3 className="font-medium text-gray-800 mb-1">Prijestupna Godina</h3>
                    <p className="text-sm text-gray-600">Provjerite prijestupne godine</p>
                  </Link>
                  <Link 
                    to="/kalkulator-trudnoce"
                    className="p-4 border border-gray-200 rounded-lg hover:bg-gray-50 transition"
                  >
                    <h3 className="font-medium text-gray-800 mb-1">Kalkulator Trudnoće</h3>
                    <p className="text-sm text-gray-600">Izračunajte termin poroda</p>
                  </Link>
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorDatuma;